import React from 'react';
import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { DataTable, type Column } from '../../components/ds';

const SAMPLE =
  'Model the trade-offs your team feels but cannot yet evidence; keep language operational, never theatrical.';

interface ScaleRow { feel: string; size: string; usage: string }

const SCALE_ROWS: ScaleRow[] = [
  { feel: 'Eyebrow / mono label', size: '0.8125–0.875rem', usage: 'Uppercase labels, badges, durations · JetBrains Mono, always' },
  { feel: 'Body standard', size: '0.9375–1rem', usage: 'Primary reading density · degular-variable' },
  { feel: 'UI heading', size: '1.125–1.5rem', usage: 'Card titles, section headers · fieldwork' },
  { feel: 'Section serif', size: '1.875–2.5rem', usage: 'Page framing inside shell · Spectral' },
  { feel: 'Hero / ceremonial', size: '>3rem', usage: 'Marketing embeds sparingly · Spectral' },
];

const SCALE_COLUMNS: Column<ScaleRow>[] = [
  { key: 'feel', header: 'Token feel', width: '26%', render: (r) => <span className="text-[var(--text-pri)]">{r.feel}</span> },
  { key: 'size', header: 'Size', width: '20%', render: (r) => <span className="font-mono text-[var(--mustard)]">{r.size}</span> },
  { key: 'usage', header: 'Usage' },
];

interface FloorRow { where: string; floor: string; why: string }

const FLOOR_ROWS: FloorRow[] = [
  { where: 'Public site', floor: 'Body 18px · labels 14px · nothing under 13px', why: 'Read by people who have never seen it before. Held by the site\'s layout check.' },
  { where: 'Standards pages and documents', floor: 'Nothing under 13px', why: 'Reference reading, not dense screens.' },
  { where: 'The app', floor: 'Named steps 14 / 16 / 17px · nothing under 12px', why: 'Dense working screens — counts, chips, inline hints. Held by the generated type floor in index.css.' },
  { where: 'Inside a figure', floor: 'Labels may be smaller', why: 'A caption on a drawing, such as the home page card. Keep it to figures.' },
];

const FLOOR_COLUMNS: Column<FloorRow>[] = [
  { key: 'where', header: 'Where', width: '24%', render: (r) => <span className="text-[var(--text-pri)]">{r.where}</span> },
  { key: 'floor', header: 'Floor', width: '36%', render: (r) => <span className="font-mono text-[var(--mustard)]">{r.floor}</span> },
  { key: 'why', header: 'Why' },
];

interface MonoRow { body: string; mono: string }

const MONO_ROWS: MonoRow[] = [
  { body: 'text-xs (12px)', mono: 'text-sm (14px)' },
  { body: 'text-sm (14px)', mono: 'text-base (16px)' },
  { body: 'text-base (16px)', mono: 'text-lg (18px)' },
];

const MONO_COLUMNS: Column<MonoRow>[] = [
  { key: 'body', header: 'Adjacent body size', width: '40%', render: (r) => <span className="font-mono text-[var(--mustard)]">{r.body}</span> },
  { key: 'mono', header: 'Use for mono', render: (r) => <span className="font-mono text-[var(--text-pri)]">{r.mono}</span> },
];

export const PrioritiesAITypographyPage: React.FC = () => (
  <>
    <ProductDesignPageHeader
      title="Typography · product readability"
      eyebrow="Type · hierarchy"
      subtitle={
        <>
          Four fonts. Each has a distinct semantic role — never interchangeable. Loaded from Typekit kit{' '}
          <code className="font-mono text-[var(--mustard)]">upa8wrl</code> (degular-variable + fieldwork) plus
          Google Fonts for Spectral + JetBrains Mono.
        </>
      }
    />

    <ProductDesignSection title="Role map" hint="These families are enforced through Tailwind aliases (font-body / font-heading / font-display / font-mono) across both the React app and visual language HTML pages.">
      <div className="space-y-10">
        <div className="space-y-2">
          <p className="font-mono text-[13px] font-medium uppercase tracking-[0.2em] text-[var(--p-label)]">
            Spectral · display / narration
          </p>
          <p className="text-4xl leading-tight text-[var(--text-pri)]" style={{ fontFamily: "'Spectral', Georgia, serif" }}>
            Prioritize with evidence, not applause.
          </p>
          <p className="text-xs text-[var(--text-dim)]">Page titles · diagram hero lines · editorial pull quotes · ceremonial labels</p>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-[13px] font-medium uppercase tracking-[0.2em] text-[var(--p-label)]">fieldwork · UI headings / structure</p>
          <p className="text-2xl font-semibold text-[var(--text-pri)]" style={{ fontFamily: "'fieldwork', 'Work Sans', system-ui, sans-serif" }}>
            Section Titles · Navigation Items · Card Headings
          </p>
          <p className="text-xs text-[var(--text-dim)]">Section headers · nav labels · card titles · modal headings</p>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-[13px] font-medium uppercase tracking-[0.2em] text-[var(--p-label)]">degular-variable · body / UI copy · San Francisco when Degular is not available</p>
          <p className="text-lg text-[var(--text-sec)] leading-relaxed" style={{ fontFamily: "'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif" }}>
            {SAMPLE}
          </p>
          <p className="text-xs text-[var(--text-dim)]">Body text · descriptions · tooltips · table content · UI copy · session prompts</p>
        </div>

        <div className="space-y-2">
          <p className="font-mono text-[13px] font-medium uppercase tracking-[0.2em] text-[var(--p-label)]">
            JetBrains Mono · eyebrows / instrumentation
          </p>
          <p className="text-sm font-mono text-[var(--mustard)] tracking-[0.16em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            WORKSPACE · CYCLE-04 · READY
          </p>
          <p className="text-xs text-[var(--text-dim)]">Eyebrows · status codes · durations · breadcrumbs · diagram labels · technical tags</p>
        </div>
      </div>
    </ProductDesignSection>

    <ProductDesignSection title="Product scale cadence">
      <DataTable
        columns={SCALE_COLUMNS}
        rows={SCALE_ROWS}
        getRowKey={(r) => r.feel}
        density="compact"
        bordered={false}
        caption="Product type scale"
        captionHidden
      />
    </ProductDesignSection>

    <ProductDesignSection
      title="JetBrains Mono sizing rule"
      hint="JetBrains Mono renders ~15–20% smaller than degular-variable or fieldwork at the same point size. Always bump mono text up one Tailwind step to compensate."
    >
      <DataTable
        columns={MONO_COLUMNS}
        rows={MONO_ROWS}
        getRowKey={(r) => r.body}
        density="compact"
        bordered={false}
        caption="Mono size compensation"
        captionHidden
      />
    </ProductDesignSection>

    <ProductDesignSection
      title="Size floors"
      hint="How small text may go, by where it is read. Two numbers, each with a reason."
    >
      <DataTable
        columns={FLOOR_COLUMNS}
        rows={FLOOR_ROWS}
        getRowKey={(r) => r.where}
        density="compact"
        bordered={false}
        caption="Text size floors"
        captionHidden
      />
    </ProductDesignSection>

    <ProductDesignSection title="Font loading — two sources required">
      <div className="space-y-3 text-sm text-[var(--text-sec)] leading-relaxed">
        <p>Both the React app and all visual language HTML pages load fonts from the same two sources:</p>
        <pre className="font-mono text-[var(--mustard)] text-xs p-4 border border-[var(--rule)] bg-[var(--surface)] overflow-x-auto">{`<!-- Typekit kit upa8wrl: degular-variable + fieldwork -->
<link rel="preconnect" href="https://use.typekit.net" crossorigin>
<link rel="stylesheet" href="https://use.typekit.net/upa8wrl.css">
<!-- Google Fonts: Spectral + JetBrains Mono -->
<link href="https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,300;0,400;0,600;1,300&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet">`}</pre>
        <p>
          <strong className="font-medium text-[var(--text-pri)]">When Degular is not available, use San Francisco</strong>{' '}
          (the Apple system font; SF Pro in documents and decks). That holds wherever Typekit cannot load:
          a deck, a document, an email, or a page whose font request fails. On screen the stack is{' '}
          <code className="font-mono text-[0.85em] text-[var(--mustard)]">'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif</code>,
          so an Apple device falls back to San Francisco and anything else to DM Sans or its own system font.
        </p>
        <p>
          <span className="font-mono text-[var(--mustard)]">Work Sans</span> stands in for fieldwork. Spectral and
          JetBrains Mono come from Google Fonts and are free to install.
        </p>
      </div>
    </ProductDesignSection>
  </>
);
